import type { PersistedStateOptions, Serializer } from 'pinia-plugin-persistedstate';

type PersistScope = 'global' | 'user';

type PersistedStateConfig = {
  key: string;
  version: number;
  paths?: string[];
  scope?: PersistScope;
  migrate?: (state: Record<string, unknown>, version: number) => Record<string, unknown>;
};

const getUserScope = (): string | null => {
  if (!import.meta.client) return null;
  const token = useCookie<string | null>('userToken');
  const userId = useCookie<string | null>('userId');
  if (!token.value) return null;
  return userId.value || 'user';
};

const normalizeState = (value: unknown): Record<string, unknown> => {
  if (!value || typeof value !== 'object') return {};
  return value as Record<string, unknown>;
};

const createSerializer = (
  version: number,
  scope: PersistScope,
  migrate?: PersistedStateConfig['migrate']
): Serializer => ({
  serialize: (value) => {
    const scopeValue = scope === 'user' ? getUserScope() : 'global';
    return JSON.stringify({ v: version, scope: scopeValue, state: value });
  },
  deserialize: (value) => {
    if (!value) return {};
    try {
      const parsed = JSON.parse(value) as Record<string, unknown>;
      if (parsed && typeof parsed === 'object' && 'state' in parsed) {
        const storedVersion = typeof parsed.v === 'number' ? parsed.v : 0;
        const storedScope = typeof parsed.scope === 'string' ? parsed.scope : 'global';
        if (scope === 'user') {
          const currentScope = getUserScope();
          if (!currentScope || storedScope !== currentScope) return {};
        }
        const storedState = normalizeState(parsed.state);
        if (storedVersion === version) return storedState;
        if (migrate) return normalizeState(migrate(storedState, storedVersion));
        return {};
      }
      const legacyState = normalizeState(parsed);
      if (migrate) return normalizeState(migrate(legacyState, 0));
      return legacyState;
    } catch {
      return {};
    }
  }
});

export const createPersistedState = ({
  key,
  version,
  paths,
  scope = 'global',
  migrate
}: PersistedStateConfig): PersistedStateOptions => ({
  key,
  paths,
  serializer: createSerializer(version, scope, migrate)
});
